import React, { useContext, useEffect, useMemo, useState } from 'react';
import Modal from 'ds/components/modal';
import CopyToClipboard from 'react-copy-to-clipboard';
import { routeContext } from 'react-router5';
import { useLocale } from 'locale';
import HomeDoneIcon from 'assets/svg/home-done.svg';
import { shareActionTypeToIcon, ShareEmailSlimBulletin, shareHandler, ShareType } from 'helpers/share-helpers';
import { isServer } from 'utils';
import { routeByPoiType } from 'utils/marketplaceRoutes';
import { AfterUploadBulletinModalPoi } from 'store/state/app';
import { useScreenBreakpoint } from 'consts/breakpoints';
import {
  ModalContentRoot,
  ModalContentInfoSection,
  ModalContentBannerSection,
  Description,
  HeaderTitle,
  IconWrapper,
  LinksWrapper,
  SocialLink,
  SocialLinkWrapper,
  BannerImage,
  CopyTooltip,
  AnimatedIconWrapper,
  AnimatedIcon,
} from './styled';
import { BannerLinkWithEvents } from 'components/menora-insurance-banner/BannerLinkWithEvents';
import { getBanner } from 'components/menora-insurance-banner/config';
import { SmallText } from 'ds/components/typography';
import OkIcon from 'assets/svg/ok-check.svg';


interface AfterUploadModalViewProps {
  popupStatus?: 'UPLOAD' | 'UPDATE' | null;
  poi: AfterUploadBulletinModalPoi | null;
  isPrivateUser: boolean;
  isModalOpen?: boolean;
  onClosePopup: () => void;
}

export const AfterUploadModalView: React.FC<AfterUploadModalViewProps> = ({
  poi,
  popupStatus,
  isPrivateUser,
  onClosePopup,
}) => {
  const { t } = useLocale();
  const router = useContext(routeContext);
  const isMobile = useScreenBreakpoint() < 3;
  const [ copied, setCopied ] = useState<boolean | null>(null);
  const bannerConfig = useMemo(() => (
    getBanner('uploadBulletinPopup', isPrivateUser ? 'privateUser' : 'agent')
  ), [ poi && poi.id, isPrivateUser ]);

  const onClose = () => onClosePopup();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(null), 4000);
    }
    return () => clearTimeout(timer);
  }, [ copied, setCopied ]);

  const handleOnCopy = React.useCallback(() => {
    if (!copied) setCopied(true);
    return false;
  }, [ copied, setCopied ]);

  if (!popupStatus || !poi) return null;

  const isNewListing = popupStatus === 'UPLOAD';

  const onSocialShare = (type: ShareType) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const poiForShare: ShareEmailSlimBulletin = {
      id: poi.id,
      type: 'ShareEmailSlimBulletin',
      bulletinType: poi.type,
    };
    shareHandler({
      type,
      url: window.location.href,
      shareViaEmailOptions: {
        poi: poiForShare,
        t,
      },
    });
  };

  const linkRoute = routeByPoiType(poi.type);
  const copyText = isServer() || !linkRoute ? '' : (window.location.origin + router.router.buildUrl(linkRoute, { id: poi.id }));
  const withBanner = Boolean(bannerConfig);

  const renderTooltip = () => copied ? (
    <CopyTooltip>
      <SmallText>{t('content.copied')}</SmallText>
    </CopyTooltip>
  ) : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      mobileModalPositionBottom
      closeButtonHorizontalPosition={withBanner && !isMobile ? 'right' : 'left'}
    >
      <ModalContentRoot isMobile={isMobile}>
        <ModalContentInfoSection>
          <IconWrapper>
            <HomeDoneIcon />
          </IconWrapper>
          <HeaderTitle weight="bold">{t('uploadBulletin.afterUpload.modal.title', { isNewListing })}</HeaderTitle>
          <Description>{t('uploadBulletin.afterUpload.modal.description')}</Description>
          <LinksWrapper>
            <SocialLinkWrapper onClick={onSocialShare(ShareType.Facebook)}>
              <SocialLink>
                {shareActionTypeToIcon[ShareType.Facebook]}
              </SocialLink>
            </SocialLinkWrapper>
            <SocialLinkWrapper>
              <CopyToClipboard text={copyText} onCopy={handleOnCopy}>
                <SocialLink>
                  <AnimatedIconWrapper showOk={copied}>
                    <AnimatedIcon>
                      {shareActionTypeToIcon[ShareType.CopyLink]}
                    </AnimatedIcon>
                    <AnimatedIcon>
                      <OkIcon />
                    </AnimatedIcon>
                  </AnimatedIconWrapper>
                  {renderTooltip()}
                </SocialLink>
              </CopyToClipboard>
            </SocialLinkWrapper>
            <SocialLinkWrapper onClick={onSocialShare(ShareType.Email)}>
              <SocialLink>
                {shareActionTypeToIcon[ShareType.Email]}
              </SocialLink>
            </SocialLinkWrapper>
          </LinksWrapper>
        </ModalContentInfoSection>
        {isPrivateUser && (
          <ModalContentBannerSection>
            {withBanner ? (
              <BannerLinkWithEvents
                bannerId={bannerConfig.analyticsBannerId}
                href={bannerConfig.href}
                bulletinId={poi.id}
              >
                <BannerImage
                  src={isMobile ? bannerConfig.imageUrlMobile : bannerConfig.imageUrlDesktop}
                  alt={bannerConfig.imageAlt}
                  isMobile={isMobile}
                />
              </BannerLinkWithEvents>
            ) : null}
          </ModalContentBannerSection>
        )}
      </ModalContentRoot>
    </Modal>
  );
};
